/**
 * Helpers for writing a viz's live controls back into the `viz_params` carried by
 * a saved conversation. See `XwaiV2StreamService.persistVizParams`, which applies
 * the resulting patch.
 */

/**
 * Equality for a single viz param. `?? null` folds undefined into null so an
 * absent value and an explicitly cleared one compare equal; arrays and objects
 * (e.g. a shot's `scaling` pair) are compared by value, since they are rebuilt on
 * every read and would otherwise always look changed.
 */
export function vizParamEqual(a: unknown, b: unknown): boolean {
  const left = a ?? null;
  const right = b ?? null;
  if (left === right) return true;
  if (
    left === null ||
    right === null ||
    typeof left !== "object" ||
    typeof right !== "object"
  ) {
    return false;
  }
  return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * One control's contribution to a patch: its param name, its current value, and
 * the seed the control falls back to when the agent omitted that param.
 */
export type VizParamEntry = [key: string, value: unknown, seed?: unknown];

/**
 * Builds the patch for `persistVizParams` from a viz's live controls.
 *
 * A control still sitting on its seed while the param is absent from `current` is
 * dropped. Without that, the viz components' `linkedSignal` fallbacks (a "—"
 * placeholder for an unknown model type, `false` for `show_shots`, the first
 * panel of a shot) would be written back on first render — persisting invented
 * values and flagging the conversation as edited before the user touched
 * anything. Once a param exists it is always sent, so clearing it back to null
 * still persists.
 */
export function vizParamsPatch(
  current: Record<string, unknown>,
  entries: VizParamEntry[],
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const [key, value, seed] of entries) {
    if (current[key] === undefined && vizParamEqual(value, seed)) continue;
    patch[key] = value;
  }
  return patch;
}

/**
 * Merges a patch into a viz's `viz_params` in place and reports whether anything
 * actually changed — the caller uses that to decide if the conversation now has
 * unsaved edits. Values equal to what's already stored are skipped, so re-running
 * this with an untouched viz is a no-op.
 */
export function applyVizParams(
  params: Record<string, unknown>,
  patch: Record<string, unknown>,
): boolean {
  let changed = false;
  for (const [key, value] of Object.entries(patch)) {
    if (!vizParamEqual(params[key], value)) {
      params[key] = value;
      changed = true;
    }
  }
  return changed;
}
